import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import {
  ArrowLeft,
  CalendarClock,
  Check,
  ExternalLink,
  Mail,
  MessageCircle,
  NotebookPen,
  Phone,
  PhoneCall,
  X,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import {
  STATUSES,
  metaOf,
  sourceLabel,
  useAddEvent,
  useLead,
  useLeadEvents,
  useTemplates,
  useUpdateLead,
  type Lead,
  type LeadEvent,
  type Template,
} from '@/lib/leads';
import {
  fillTemplate,
  lowerFirst,
  phoneDisplay,
  phoneE164,
  primaryChannel,
  serviceLabel,
  telHref,
  whatsappHref,
} from '@/lib/contact';
import { ago, calendarDate, followUpPresets, fromLocalInput, theirTime, toLocalInput, when } from '@/lib/format';
import { Page, PageHeader } from '@/components/Shell';
import { LoadError, SectionLabel } from '@/components/ui';

export type Composer = null | 'whatsapp' | 'email' | 'call';

export function LeadScreen({ leadId, reach }: { leadId: string; reach?: Composer }) {
  const lead = useLead(leadId);
  const update = useUpdateLead(leadId);

  // Opening a lead is reading it.
  const unread = lead.data && !lead.data.read_at;
  useEffect(() => {
    if (unread) update.mutate({ read_at: new Date().toISOString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unread]);

  const back = (
    <Link to="/enquiries" className="btn btn-ghost btn-icon -ml-2 lg:hidden" aria-label="Back to enquiries">
      <ArrowLeft className="size-5" aria-hidden />
    </Link>
  );

  if (lead.isPending) {
    return (
      <>
        <PageHeader title="Enquiry" back={back} />
        <Page>
          <div className="grid gap-3" aria-busy="true">
            <div className="skeleton h-12 w-full max-w-md" />
            <div className="skeleton h-32 w-full" />
          </div>
        </Page>
      </>
    );
  }
  if (lead.error || !lead.data) {
    return (
      <>
        <PageHeader title="Enquiry" back={back} />
        <Page>
          {lead.error ? (
            <LoadError error={lead.error} retry={() => lead.refetch()} />
          ) : (
            <p className="text-fg-dim">This enquiry does not exist, or it was removed.</p>
          )}
        </Page>
      </>
    );
  }

  return <LeadView lead={lead.data} back={back} reach={reach ?? null} />;
}

function LeadView({ lead, back, reach }: { lead: Lead; back: ReactNode; reach: Composer }) {
  const [composer, setComposer] = useState<Composer>(reach);
  const meta = metaOf(lead);
  const service = serviceLabel(meta.interest, 'name');
  const e164 = phoneE164(lead);
  const primary = primaryChannel(lead);
  const theirs = theirTime(lead.timezone);

  return (
    <>
      <PageHeader title={lead.name} back={back}>
        <StatusSelect lead={lead} />
      </PageHeader>
      <Page>
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_22rem] lg:gap-10">
          <div className="grid min-w-0 grid-cols-1 content-start gap-8">
            {/* ---- reach them ---- */}
            <section aria-label="Contact">
              <p className="text-fg-dim">
                {[lead.company, service ? `wants ${lowerFirst(service)}` : null].filter(Boolean).join(' · ') ||
                  'No company or service given.'}
              </p>
              <div className="mt-4 grid grid-cols-3 gap-2 sm:flex sm:flex-wrap">
                <ActionButton
                  icon={MessageCircle}
                  label="WhatsApp"
                  primary={primary === 'whatsapp'}
                  disabled={!e164}
                  pressed={composer === 'whatsapp'}
                  onClick={() => setComposer(composer === 'whatsapp' ? null : 'whatsapp')}
                />
                <ActionButton
                  icon={Mail}
                  label="Email"
                  primary={primary === 'email'}
                  disabled={!lead.email}
                  pressed={composer === 'email'}
                  onClick={() => setComposer(composer === 'email' ? null : 'email')}
                />
                {e164 ? (
                  <a
                    href={telHref(e164)}
                    onClick={() => setComposer('call')}
                    className="btn btn-quiet btn-lg max-sm:flex-col max-sm:gap-1 max-sm:py-2 max-sm:text-xs"
                  >
                    <Phone className="size-5" aria-hidden /> Call
                  </a>
                ) : (
                  <ActionButton icon={Phone} label="Call" disabled onClick={() => {}} />
                )}
              </div>
              <dl className="mt-4 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                <Detail label="Phone">
                  {lead.phone ? <span className="num">{phoneDisplay(lead)}</span> : <Dim>none</Dim>}
                  {lead.phone && !e164 && <span className="ml-2 text-xs text-warn">can't read this number</span>}
                </Detail>
                <Detail label="Email">
                  {lead.email ? <span className="break-all">{lead.email}</span> : <Dim>none</Dim>}
                </Detail>
                {theirs && (
                  <Detail label="Their time">
                    <span className="tnum">{theirs}</span> <Dim>({lead.timezone})</Dim>
                  </Detail>
                )}
              </dl>

              {composer === 'whatsapp' && e164 && (
                <WhatsAppComposer lead={lead} e164={e164} onClose={() => setComposer(null)} />
              )}
              {composer === 'email' && lead.email && <EmailComposer lead={lead} onClose={() => setComposer(null)} />}
              {composer === 'call' && <CallOutcome lead={lead} onClose={() => setComposer(null)} />}
            </section>

            {/* ---- what they said ---- */}
            <section aria-labelledby="said">
              <SectionLabel>
                <span id="said">What they wrote</span>
              </SectionLabel>
              {lead.bottleneck ? (
                <blockquote className="whitespace-pre-wrap rounded-lg border border-edge bg-bg-raised px-4 py-3 leading-relaxed">
                  {lead.bottleneck}
                </blockquote>
              ) : (
                <p className="text-sm text-fg-dim">Nothing written. This lead was added by hand.</p>
              )}
              <dl className="mt-4 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                {lead.preferred_date && (
                  <Detail label="Call on">
                    <span className="tnum">{calendarDate(lead.preferred_date)}</span> <Dim>· 9 am – 9 pm IST</Dim>
                  </Detail>
                )}
                {lead.website && (
                  <Detail label="Website">
                    <a
                      href={/^https?:/.test(lead.website) ? lead.website : `https://${lead.website}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 underline"
                    >
                      {lead.website.replace(/^https?:\/\//, '')} <ExternalLink className="size-3" aria-hidden />
                    </a>
                  </Detail>
                )}
                <Detail label="Came from">
                  {sourceLabel(lead.source)}
                  {meta.source_page && <span className="block break-all text-xs text-fg-dim">{meta.source_page}</span>}
                </Detail>
                <Detail label="Received">
                  <span className="tnum">{when(lead.created_at)}</span>
                </Detail>
                <Detail label="Ref">
                  <span className="num">{lead.ref}</span>
                </Detail>
              </dl>
            </section>

            <FollowUp lead={lead} />
          </div>

          {/* ---- the record ---- */}
          <aside aria-label="Timeline" className="lg:border-l lg:border-edge lg:pl-10">
            <NoteComposer leadId={lead.id} />
            <Timeline leadId={lead.id} />
          </aside>
        </div>
      </Page>
    </>
  );
}

function Dim({ children }: { children: ReactNode }) {
  return <span className="text-fg-faint">{children}</span>;
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-3 py-1">
      <dt className="w-24 shrink-0 text-fg-dim">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}

function ActionButton({
  icon: Icon,
  label,
  primary,
  disabled,
  pressed,
  onClick,
}: {
  icon: typeof Phone;
  label: string;
  primary?: boolean;
  disabled?: boolean;
  pressed?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={pressed}
      className={`btn btn-lg max-sm:flex-col max-sm:gap-1 max-sm:py-2 max-sm:text-xs ${
        primary ? 'btn-primary' : 'btn-quiet'
      } ${pressed && !primary ? '!bg-bg-sunk' : ''}`}
    >
      <Icon className="size-5" aria-hidden /> {label}
    </button>
  );
}

/* ----------------------------------------------------------------- status */

function StatusSelect({ lead }: { lead: Lead }) {
  const update = useUpdateLead(lead.id);
  return (
    <label className="relative">
      <span className="sr-only">Status</span>
      <select
        className="input !min-h-9 !py-1.5 !pr-8 text-sm font-semibold"
        value={lead.status}
        disabled={update.isPending}
        onChange={(e) => update.mutate({ status: e.target.value })}
      >
        {STATUSES.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/* --------------------------------------------------------------- whatsapp */

function vars(lead: Lead) {
  const meta = metaOf(lead);
  return {
    name: lead.name,
    company: lead.company,
    service: lowerFirst(serviceLabel(meta.interest, 'name')),
    date: lead.preferred_date ? calendarDate(lead.preferred_date) : null,
  };
}

function TemplatePicker({
  templates,
  active,
  onPick,
}: {
  templates: Template[];
  active: string | null;
  onPick: (t: Template) => void;
}) {
  if (!templates.length) return null;
  return (
    <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1" role="group" aria-label="Saved replies">
      {templates.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onPick(t)}
          aria-pressed={active === t.id}
          className={`btn btn-sm shrink-0 ${active === t.id ? 'btn-ink' : 'btn-quiet'}`}
        >
          {t.name}
        </button>
      ))}
    </div>
  );
}

function Panel({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="mt-4 rounded-lg border border-edge-strong bg-bg-raised p-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm font-semibold">{title}</p>
        <button type="button" onClick={onClose} className="btn btn-ghost btn-sm btn-icon -mr-2" aria-label="Close">
          <X className="size-4" aria-hidden />
        </button>
      </div>
      {children}
    </div>
  );
}

function WhatsAppComposer({ lead, e164, onClose }: { lead: Lead; e164: string; onClose: () => void }) {
  const templates = useTemplates();
  const wa = useMemo(() => (templates.data ?? []).filter((t) => t.channel === 'whatsapp'), [templates.data]);
  const [picked, setPicked] = useState<Template | null>(null);
  const [text, setText] = useState('');
  const [handedOver, setHandedOver] = useState(false);
  const addEvent = useAddEvent(lead.id);
  const update = useUpdateLead(lead.id);

  // Start from the first saved reply once they load.
  useEffect(() => {
    if (!picked && wa[0]) {
      setPicked(wa[0]);
      setText(fillTemplate(wa[0].body, vars(lead)));
    }
  }, [wa, picked, lead]);

  return (
    <Panel title="WhatsApp" onClose={onClose}>
      <TemplatePicker
        templates={wa}
        active={picked?.id ?? null}
        onPick={(t) => {
          setPicked(t);
          setText(fillTemplate(t.body, vars(lead)));
        }}
      />
      <label htmlFor="wa-text" className="sr-only">
        Message
      </label>
      <textarea id="wa-text" className="input mt-2" rows={5} value={text} onChange={(e) => setText(e.target.value)} />
      {!handedOver ? (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <a
            href={whatsappHref(e164, text)}
            target="_blank"
            rel="noreferrer"
            className="btn btn-primary"
            onClick={() => {
              setHandedOver(true);
              addEvent.mutate({
                kind: 'whatsapp',
                body: text,
                meta: { template: picked?.name ?? null, to: e164 },
              });
            }}
          >
            <MessageCircle className="size-4" aria-hidden /> Open in WhatsApp
          </a>
          <p className="hint">The message opens in WhatsApp ready to send. You press send there.</p>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-3 rounded-md bg-bg-sunk px-3 py-2.5 text-sm">
          <Check className="size-4 text-ok" aria-hidden />
          <span>Logged as prepared in WhatsApp.</span>
          {lead.status === 'new' && (
            <button
              type="button"
              className="btn btn-quiet btn-sm ml-auto"
              disabled={update.isPending}
              onClick={() =>
                update.mutate({ status: 'contacted', last_contacted_at: new Date().toISOString() }, { onSuccess: onClose })
              }
            >
              I sent it: mark contacted
            </button>
          )}
        </div>
      )}
    </Panel>
  );
}

/* ------------------------------------------------------------------ email */

function EmailComposer({ lead, onClose }: { lead: Lead; onClose: () => void }) {
  const templates = useTemplates();
  const mails = useMemo(() => (templates.data ?? []).filter((t) => t.channel === 'email'), [templates.data]);
  const [picked, setPicked] = useState<Template | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!picked && mails[0]) choose(mails[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mails]);

  function choose(t: Template) {
    setPicked(t);
    setSubject(fillTemplate(t.subject ?? '', vars(lead)));
    setBody(fillTemplate(t.body, vars(lead)));
  }

  async function send() {
    setState('sending');
    setError(null);
    const { data, error } = await supabase.functions.invoke('desk-mail', {
      body: { lead_id: lead.id, subject, body },
    });
    if (error || !data?.ok) {
      let detail = data?.error as string | undefined;
      if (!detail && error && 'context' in error) {
        detail = await (error.context as Response).json().then((j) => j.error).catch(() => undefined);
      }
      setError(detail ?? error?.message ?? 'The email was not sent.');
      setState('idle');
      return;
    }
    setState('sent');
  }

  if (state === 'sent') {
    return (
      <Panel title="Email" onClose={onClose}>
        <p className="flex items-center gap-2 text-sm">
          <Check className="size-4 text-ok" aria-hidden /> Sent to {lead.email}. A copy is in the team inbox.
        </p>
      </Panel>
    );
  }

  return (
    <Panel title={`Email to ${lead.email}`} onClose={onClose}>
      <TemplatePicker templates={mails} active={picked?.id ?? null} onPick={choose} />
      <div className="mt-2 grid gap-2">
        <label htmlFor="mail-subject" className="sr-only">
          Subject
        </label>
        <input
          id="mail-subject"
          className="input"
          placeholder="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
        <label htmlFor="mail-body" className="sr-only">
          Message
        </label>
        <textarea id="mail-body" className="input" rows={9} value={body} onChange={(e) => setBody(e.target.value)} />
      </div>
      {error && (
        <p role="alert" className="error-text mt-2">
          {error}
        </p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="btn btn-primary"
          disabled={state === 'sending' || !subject.trim() || !body.trim()}
          onClick={send}
        >
          <Mail className="size-4" aria-hidden /> {state === 'sending' ? 'Sending…' : 'Send email'}
        </button>
        <p className="hint">Sent as hello@bitbew.com. Replies arrive in the team inbox.</p>
      </div>
    </Panel>
  );
}

/* ------------------------------------------------------------------- call */

function CallOutcome({ lead, onClose }: { lead: Lead; onClose: () => void }) {
  const addEvent = useAddEvent(lead.id);
  const update = useUpdateLead(lead.id);
  const [done, setDone] = useState<string | null>(null);

  function log(outcome: 'answered' | 'no_answer' | 'call_back', label: string) {
    addEvent.mutate({ kind: 'call', body: label, meta: { outcome } });
    if (outcome === 'answered') {
      update.mutate({
        last_contacted_at: new Date().toISOString(),
        ...(lead.status === 'new' ? { status: 'contacted' } : {}),
      });
    }
    if (outcome === 'call_back') {
      const tomorrow = followUpPresets()[0];
      update.mutate({ next_action_at: tomorrow.at, next_action_note: 'Call back' });
    }
    setDone(label);
  }

  return (
    <Panel title="How did the call go?" onClose={onClose}>
      {done ? (
        <p className="flex items-center gap-2 text-sm">
          <Check className="size-4 text-ok" aria-hidden /> Logged: {done.toLowerCase()}.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn btn-quiet" onClick={() => log('answered', 'Spoke to them')}>
            <PhoneCall className="size-4" aria-hidden /> Spoke to them
          </button>
          <button type="button" className="btn btn-quiet" onClick={() => log('no_answer', 'No answer')}>
            No answer
          </button>
          <button type="button" className="btn btn-quiet" onClick={() => log('call_back', 'Call back tomorrow')}>
            <CalendarClock className="size-4" aria-hidden /> Call back tomorrow
          </button>
        </div>
      )}
    </Panel>
  );
}

/* -------------------------------------------------------------- follow-up */

function FollowUp({ lead }: { lead: Lead }) {
  const update = useUpdateLead(lead.id);
  const [editing, setEditing] = useState(false);
  const [at, setAt] = useState(toLocalInput(lead.next_action_at));
  const [note, setNote] = useState(lead.next_action_note ?? '');
  const due = lead.next_action_at && new Date(lead.next_action_at).getTime() <= Date.now();

  function save(nextAt: string | null, nextNote: string) {
    update.mutate(
      { next_action_at: nextAt, next_action_note: nextAt ? nextNote.trim() || null : null },
      { onSuccess: () => setEditing(false) },
    );
  }

  return (
    <section aria-labelledby="follow">
      <SectionLabel>
        <span id="follow">Follow-up</span>
      </SectionLabel>
      {lead.next_action_at && !editing ? (
        <div
          className={`flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3 ${
            due ? 'border-warn/40 bg-warn-tint' : 'border-edge bg-bg-raised'
          }`}
        >
          <CalendarClock className={`size-5 ${due ? 'text-warn' : 'text-fg-dim'}`} aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="font-semibold">
              {due ? 'Due ' : ''}
              <span className="tnum">{when(lead.next_action_at)}</span>
            </p>
            {lead.next_action_note && <p className="text-sm text-fg-dim">{lead.next_action_note}</p>}
          </div>
          <button type="button" className="btn btn-quiet btn-sm" onClick={() => setEditing(true)}>
            Change
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => save(null, '')}>
            Done
          </button>
        </div>
      ) : editing || !lead.next_action_at ? (
        <div className="grid gap-3 rounded-lg border border-edge bg-bg-raised p-4">
          <div className="flex flex-wrap gap-2">
            {followUpPresets().map((p) => (
              <button
                key={p.label}
                type="button"
                className="btn btn-quiet btn-sm"
                onClick={() => setAt(toLocalInput(p.at))}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="grid gap-2 sm:grid-cols-[13rem_1fr]">
            <label className="sr-only" htmlFor="fu-at">
              When
            </label>
            <input
              id="fu-at"
              type="datetime-local"
              className="input num"
              value={at}
              onChange={(e) => setAt(e.target.value)}
            />
            <label className="sr-only" htmlFor="fu-note">
              What to do
            </label>
            <input
              id="fu-note"
              className="input"
              placeholder="What to do, e.g. send the quote"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn btn-ink btn-sm"
              disabled={!at || update.isPending}
              onClick={() => save(fromLocalInput(at), note)}
            >
              Set follow-up
            </button>
            {editing && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}>
                Cancel
              </button>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}

/* --------------------------------------------------------------- timeline */

function NoteComposer({ leadId }: { leadId: string }) {
  const addEvent = useAddEvent(leadId);
  const [text, setText] = useState('');
  return (
    <form
      className="mb-6 grid gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!text.trim()) return;
        addEvent.mutate({ kind: 'note', body: text.trim() }, { onSuccess: () => setText('') });
      }}
    >
      <label htmlFor="note" className="text-sm font-semibold">
        Add a note
      </label>
      <textarea
        id="note"
        className="input !min-h-20"
        rows={3}
        placeholder="Budget, timing, who decides…"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div>
        <button type="submit" className="btn btn-quiet btn-sm" disabled={!text.trim() || addEvent.isPending}>
          <NotebookPen className="size-4" aria-hidden /> Save note
        </button>
      </div>
    </form>
  );
}

const EVENT_LABEL: Record<string, string> = {
  created: 'Enquiry received',
  status: 'Status changed',
  note: 'Note',
  call: 'Call',
  whatsapp: 'WhatsApp prepared',
  email_out: 'Email sent',
  email_in: 'Email received',
  follow_up: 'Follow-up set',
  converted: 'Won',
  edited: 'Edited',
};

function Timeline({ leadId }: { leadId: string }) {
  const events = useLeadEvents(leadId);
  if (events.isPending) return <div className="skeleton h-24 w-full" aria-busy="true" />;
  if (events.error) return <LoadError error={events.error} retry={() => events.refetch()} />;
  return (
    <section aria-labelledby="timeline-h">
      <SectionLabel>
        <span id="timeline-h">Timeline</span>
      </SectionLabel>
      <ol className="relative grid gap-5 border-l border-edge pl-5">
        {events.data.map((e) => (
          <TimelineItem key={e.id} e={e} />
        ))}
      </ol>
    </section>
  );
}

function TimelineItem({ e }: { e: LeadEvent }) {
  const meta = (e.meta ?? {}) as Record<string, unknown>;
  let title = EVENT_LABEL[e.kind] ?? e.kind;
  let detail: ReactNode = e.body;

  if (e.kind === 'status') {
    const to = STATUSES.find((s) => s.value === meta.to)?.label ?? String(meta.to);
    title = `Moved to ${to}`;
    detail = null;
  }
  if (e.kind === 'created') {
    const service = serviceLabel(meta.interest as string, 'name');
    detail = [sourceLabel(String(meta.source ?? 'website')), service].filter(Boolean).join(' · ');
  }
  if (e.kind === 'follow_up') {
    title = meta.at ? `Follow-up for ${when(String(meta.at))}` : 'Follow-up cleared';
  }
  if (e.kind === 'email_out' && meta.subject) {
    detail = (
      <>
        <span className="font-semibold">{String(meta.subject)}</span>
        <span className="mt-1 line-clamp-3 block whitespace-pre-wrap">{e.body}</span>
      </>
    );
  }
  if (e.kind === 'email_in') {
    title = meta.from ? `Email from ${String(meta.from)}` : 'Email received';
    detail = (
      <>
        {meta.subject ? <span className="font-semibold">{String(meta.subject)}</span> : null}
        {e.body ? (
          <span className="mt-1 block whitespace-pre-wrap text-fg">{e.body}</span>
        ) : (
          <span className="mt-1 block">The message itself is in the team inbox.</span>
        )}
      </>
    );
  }
  if (e.kind === 'whatsapp' && meta.template) {
    title = `WhatsApp prepared: ${String(meta.template)}`;
    detail = <span className="line-clamp-3 whitespace-pre-wrap">{e.body}</span>;
  }

  return (
    <li className="relative">
      <span
        className={`absolute top-1.5 -left-[1.5625rem] size-2 rounded-full ring-4 ring-paper ${
          e.kind === 'created' || e.kind === 'converted' ? 'bg-ink' : 'bg-edge-strong'
        }`}
        aria-hidden
      />
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm font-semibold">{title}</p>
        <time dateTime={e.at} className="tnum shrink-0 text-xs text-fg-faint" title={when(e.at)}>
          {ago(e.at)}
        </time>
      </div>
      {detail && <div className="mt-0.5 text-sm text-fg-dim">{detail}</div>}
    </li>
  );
}
